import { useEffect, useRef } from "react";

interface LogLine {
	stream: string;
	text: string;
	at: string;
}

interface Props {
	lines: LogLine[];
	emptyLabel?: string;
}

/** Scrolling monospace viewer. Auto-sticks to bottom when new lines
 * arrive and the user is already scrolled near the bottom. */
export function LogViewer({ lines, emptyLabel = "No logs yet." }: Props) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const stickRef = useRef(true);

	// biome-ignore lint/correctness/useExhaustiveDependencies: re-run on new lines (length serves as a stable proxy); stickRef is a ref (not a dep).
	useEffect(() => {
		if (!scrollRef.current || !stickRef.current) return;
		scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
	}, [lines.length]);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const el = e.currentTarget;
		const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
		stickRef.current = distanceFromBottom < 40;
	};

	if (lines.length === 0) {
		return (
			<p className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
				{emptyLabel}
			</p>
		);
	}

	return (
		<div
			ref={scrollRef}
			onScroll={handleScroll}
			className="max-h-80 overflow-y-auto rounded-md border bg-black/90 p-3 font-mono text-xs leading-5 text-slate-100"
		>
			{lines.map((line, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: logs are append-only; index is stable enough
					key={i}
					className={
						line.stream === "stderr"
							? "text-rose-300"
							: line.stream === "system"
								? "text-sky-300"
								: ""
					}
				>
					{line.text.replace(/\n$/, "")}
				</div>
			))}
		</div>
	);
}
